import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { withRouter, useHistory, RouteComponentProps } from 'react-router-dom';
import * as queryString from 'query-string';
import loginBackground from './images/LoginBackground.png';
import loginEnogenBackground from './images/EnogenLogin.png';
import { ReactComponent as CoreLogo } from '../../assets/images/GH-GHX-Logos-Orange.svg';
import { ReactComponent as EnogenCoreLogo } from '../../assets/images/EluminateEnogenLogos.svg';
import { Button } from '../../components/Button/Button';
import { Checkbox } from '../../components/Checkbox/Checkbox';
import { Input } from '../../components/Input/Input';
import { InfoPanel } from '../../components/InfoPanel/InfoPanel';
import { initiateOktaFlow, LAST_LOGIN_TYPE_KEY, ssoExchange, tokenExchange } from '../../logic/store/User/AuthSlice';
import { requestPasswordReset } from '../../logic/store/User/UserThunks';
import { useTheme } from 'styled-components';
import { AppDispatch, RootState, useAppDispatch, useAppSelector } from '../../logic/store/Store';
import { StyledModal } from '../../components/StyledModal/StyledModal';
import { makeDispatch } from '../../logic/Utility/Utils';
import { CoveringLoader } from '../../components/LoadingSpinner/CoveringLoader';
import { ScaleLoader } from 'react-spinners';
import { ThemeName } from '../../logic/store/UI/UISlice';
import { useUrlTheme } from '../../logic/Utility/useUrlTheme';
import { Features, getFeatureFlag } from '../../logic/store/User/ConfigSlice';
import { FORCE_AUTH_OKTA_VALUE, FORCE_AUTH_QUERY_PARAM } from '../Routing/authRedirect';

interface ILoginProps extends PropsFromRedux, RouteComponentProps
{
	changeTheme: (name: ThemeName) => void;
}

const Login: React.FC<ILoginProps> = (props: ILoginProps) =>
{
	// Get any relevant parameters from the url
	const parsed = queryString.parse(location.search);
	
	// Redux state items
	const {
		changeTheme,
		IsAuthenticated,
		IsLoginError,
		LoginErrorMessage,
		UserAuthToken,
		InitialUserFoundationId,
		InitialGrowerFoundationId,
		SsoLogin,
		PerformExchange,
		PerformSsoExchange,
		RequestPasswordReset,
	} = props;

	const theme = useTheme();
	
	const featureFlags = useAppSelector((state) => state.config.FeatureFlags);
	const allowOktaLogic = getFeatureFlag(featureFlags, Features.AllowOktaLogin);
	const dispatch = useAppDispatch();

	const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState<boolean>(false);
	const [username, setUserName] = useState<string>(localStorage.getItem('HasAcceptedTermsAndConditions') ?? '');
	const [error, setError] = useState(typeof(parsed.error) === 'string' ? parsed.error : undefined);
	const [openPasswordRecoveryModal, setOpenPasswordRecoveryModal] = useState<boolean>(false);
	const [passwordRecoveryEmail, setPasswordRecoveryEmail] = useState<string>('');
	const [isEnogenLogin, setIsEnogenLogin] = useState<boolean>(false);
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(false);
	const postLoginProgress = useSelector((state: RootState) => state.ui.PostLoginProgress);
	const ssoTokenInUrl = typeof(parsed.sso) === 'string' && !!parsed.sso.length;
	const ssoRedirectInProgress = (
		// Not logged in but there is a v5 SSO token.
		ssoTokenInUrl
		|| (
			// Have authenticated and its SSO login.
			postLoginProgress.loginType === 'sso'
			// Ensure post login process is in progress.
			&& !['none', 'done', 'error'].includes(postLoginProgress.status)
		)
	);

	useEffect(() => 
	{
		const lastAccepted = localStorage.getItem('HasAcceptedTermsAndConditions');
		if(username?.length && lastAccepted?.toLowerCase() == username?.toLowerCase() && !hasAcceptedAgreement)
		{
			setHasAcceptedAgreement(true);
		}
	}, 
	[username, hasAcceptedAgreement]);

	const userAgreement =
		<a
			target='_blank'
			rel='noopener noreferrer'
			href='https://www.syngenta-us.com/legal/useragreement.html'
			style={{ color: theme.colors.secondary }}
		>User Agreement</a>;

	useEffect(() => 
	{
		// Old-style sso support for current logistics
		if(typeof(parsed.exchange) === 'string' && parsed.exchange.length)
		{
			setIsLoading(true);
			try
			{
				PerformExchange(parsed.exchange);
			}
			finally
			{
				setIsLoading(false);
			}
		}

		// Check for new v5 sso token
		else if(typeof(parsed.sso) === 'string' && parsed.sso.length)
		{
			setIsLoading(true);
			const exchangeToken = parsed.sso;

			const execute = async () => 
			{
				try
				{
					// Fire off the SSO exchange request
					const result = await PerformSsoExchange(exchangeToken);
					const payload = result.payload as any;
					if(payload.code && payload.message)
					{
						// Failure; display the error and remove the token from the url
						setError(payload.message);
						history.push('/login', 'Post-SSO Redirect');
					}
					
					// Sucess; Redirect as instructed by postlogin listener
					history.push(postLoginProgress.landingPage);
				}
				finally
				{
					// Give a bit of time before the spinner disappears so
					// any error message shows first.
					setTimeout(() => setIsLoading(false), 100);
				}
			};
			execute();
		}
	}, [parsed.exchange, parsed.sso]);

	useUrlTheme(setIsEnogenLogin, changeTheme);

	// Auto-initiate the Okta flow for users whose last successful login was Okta
	// or for users whose deep link explicitly forces Okta
	const [autoOktaTriggered, setAutoOktaTriggered] = useState<boolean>(false);
	useEffect(() =>
	{
		if (autoOktaTriggered) return;
		if (!allowOktaLogic) return;
		if (IsAuthenticated) return;
		if (isLoading) return;
		if (error) return;
		if (ssoTokenInUrl) return;
		if (typeof parsed.exchange === 'string' && parsed.exchange.length) return;

		const forcedOkta = parsed[FORCE_AUTH_QUERY_PARAM] === FORCE_AUTH_OKTA_VALUE;
		const rememberedOkta = localStorage.getItem(LAST_LOGIN_TYPE_KEY) === 'okta';

		if ((forcedOkta || rememberedOkta) && username?.length)
		{
			setAutoOktaTriggered(true);
			dispatch(initiateOktaFlow({ email: username }));
		}
	}, [allowOktaLogic, IsAuthenticated, isLoading, error, ssoTokenInUrl, parsed.exchange, parsed[FORCE_AUTH_QUERY_PARAM], autoOktaTriggered, username, dispatch]);

	const onLogin = useCallback(() =>
	{
		if(!username?.length || !hasAcceptedAgreement)
		{
			return;
		}

		localStorage.setItem('HasAcceptedTermsAndConditions', username);
		dispatch(initiateOktaFlow({ email: username }));
	}, [username, hasAcceptedAgreement, dispatch]);

	// This handles the redirect after a successful login for all logins and
	// handles the case where someone deliberately navigates to login after auth.
	// Caveat: Initial SSO login is handled by SSO useEffect, not this one.
	useEffect(() =>
	{
		if(IsAuthenticated && postLoginProgress.landingPage && !ssoTokenInUrl) 
		{
			// Redirect to the landing page
			history.push(postLoginProgress.landingPage);
		}
	}, [postLoginProgress, IsAuthenticated, ssoTokenInUrl]);

	useEffect(() =>
	{
		if (IsLoginError && username)
		{
			setError(LoginErrorMessage);
		}
	}, [IsLoginError, LoginErrorMessage]);

	// If the auto-initiated Okta flow fails (e.g., initiateOktaFlow.rejected),
	// reset the auto-trigger gate so the login form/error can render instead
	// of leaving the user on a blank page.
	useEffect(() =>
	{
		if (IsLoginError && autoOktaTriggered)
		{
			setAutoOktaTriggered(false);
			setError(LoginErrorMessage);
		}
	}, [IsLoginError, LoginErrorMessage, autoOktaTriggered]);

	const onChangeEmail = (e) =>
	{
		const email = e.target.value;
		setError(undefined);
		setUserName(email);
		if(email?.toLowerCase() != localStorage.getItem('HasAcceptedTermsAndConditions')?.toLocaleLowerCase())
		{
			setHasAcceptedAgreement(false);
		}
	};

	const onChangeAgreement = (e) =>
	{
		const isChecked = e.target.checked;
		setHasAcceptedAgreement(isChecked);
		setError(undefined);
	};

	const onChangeRecoveryEmail = (e) =>
	{
		const email = e.target.value;

		setPasswordRecoveryEmail(email);
	};

	const sendRecoveryEmail = () =>
	{
		// Copy the state value 
		const email = passwordRecoveryEmail;

		// Close the modal
		setOpenPasswordRecoveryModal(false);

		// Clear the email state value
		setPasswordRecoveryEmail('');

		// Send the request for an email
		RequestPasswordReset({ Email: email });
	};

	const cancelRecovery = () =>
	{
		// Clear the email
		setPasswordRecoveryEmail('');

		// Close the modal
		setOpenPasswordRecoveryModal(false);
	};

	// The route for sso login includes /login but we do not want to flash the login screen.
	// Same idea while the auto-Okta redirect is being kicked off.
	if (ssoRedirectInProgress || autoOktaTriggered) 
	{
		return <></>;
	}

	return (
		<div style={{
			backgroundImage: 'url(' + (isEnogenLogin ? loginEnogenBackground : loginBackground) + ')',
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			height: '100%',
			display: 'flex'
		}}>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					flexBasis: '100%',
					alignSelf: 'center',
					justifyContent: 'space-evenly',
					height: '70vh',
					paddingLeft: '3vw',
					paddingRight: '3vw'
				}}
			>
				<div style={{ position: 'relative', alignSelf: 'center' }}>
					{
						isEnogenLogin ?
							<EnogenCoreLogo  width={'40vw'} height={'12vh'} />
							:
							<CoreLogo width={'40vw'} height={'8vh'} />
					}
				</div>
				<div style={{
					backgroundColor: theme.colors.darkGrey,
					color: 'white',
					display: 'flex',
					flexDirection: 'column',
					maxHeight: '50vh',
					maxWidth: '45vw',
					borderRadius: '15px 15px 15px 15px',
					padding: '4vw',
					opacity: 0.9,
					alignSelf: 'center'
				}}
				>
					{error && <InfoPanel variant='error' style={{maxWidth: 465, marginBottom: 15 }}>{error}</InfoPanel>}
					<form style={{ display: 'flex', flexDirection: 'column' }} onKeyUp={(evt) => evt.key === 'Enter' ? onLogin() : null}>
						<Input
							label='Email'
							type='email'
							value={username}
							onChange={onChangeEmail}
							placeholder='Enter Email Address'
							style={{ marginBottom: 10 }}
						/>
						<div style={{ alignSelf: 'flex-end' }}>
							<button
								type='button'
								onClick={() => setOpenPasswordRecoveryModal(true)}
								style={{ color: theme.colors.secondary, textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
							>Forgot Password</button>
						</div>
						<div style={{ display: 'flex', flexDirection: 'row', paddingTop: 35, paddingLeft: 5 }}>
							<Checkbox style={{ color: 'white', fontWeight: 700, fontSize: '.85rem', paddingLeft: 5, paddingRight: 10 }}
								onChange={onChangeAgreement}
								checked={hasAcceptedAgreement}>
								{'I have read and accept the conditions stated in the '}{userAgreement}
							</Checkbox>
						</div>
					</form>
					<div style={{ paddingTop: 35, display: 'flex', alignSelf: 'center' }}>
						<Button variant='outlined' width='144px' onClick={onLogin} disabled={!username?.length || !hasAcceptedAgreement}>Login</Button>
					</div>
				</div>
			</div>
			<StyledModal
				title={'Forgot Password'}
				onCancel={cancelRecovery}
				open={openPasswordRecoveryModal}
				closable={true}
				bodyStyle={{ padding: 25, paddingTop: 0 }}
				destroyOnClose={true}
			>
				<div style={{ paddingBottom: 20 }}>
					Trouble signing in? Enter your email below and hit OK to receive instructions on how to reset your password.
					<Input
						variant='light'
						label='Email'
						type='email'
						value={passwordRecoveryEmail}
						onChange={onChangeRecoveryEmail}
						placeholder='Enter Email Address'
					/>
				</div>
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
					<Button
						className='sendRecoveryEmail_Button_OK'
						variant='outlined'
						disabled={!passwordRecoveryEmail}
						onClick={sendRecoveryEmail}
					>Send Recovery Email</Button>
				</div>
			</StyledModal>
			<CoveringLoader className={isLoading ? 'loading' : ''} duration={50}>
				<ScaleLoader color={theme.colors.primary} loading={true} />
			</CoveringLoader>
		</div>
	);
};

const mapStateToProps = (state: RootState) => ({
	IsAuthenticated: state.auth.isAuthenticated,
	IsLoginError: state.auth.isLoginError,
	LoginErrorMessage: state.auth.loginErrorMessage,
	UserAuthToken: state.auth.userAuthToken,
	InitialUserFoundationId: state.ui.InitialUserFoundationId,
	InitialGrowerFoundationId: state.ui.InitialGrowerFoundationId,
	SsoLogin: state.userInfo.SsoLogin,
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {
		PerformExchange: (token: string) => dispatch(tokenExchange(token)),
		PerformSsoExchange: (token: string) => dispatch(ssoExchange({ token })),
		RequestPasswordReset: makeDispatch(dispatch, requestPasswordReset),
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const LoginComponent = withRouter(connector(Login));
